// UPI collect helpers (Android UPI Setting / "UPI Print" button). Builds the
// standard upi://pay deep link encoded into a QR that any UPI app can scan.

import { PosConfig, Transaction } from '../types';
import { generateQrMatrix } from './qrcode';
import { getAppSettingsSync } from './db';
import { isOn } from './appSettings';

// True when a UPI VPA is configured, i.e. UPI QRs can be produced at all.
export function upiConfigured(config: PosConfig): boolean {
  return !!config.upiId?.trim();
}

// Whether this bill should carry a scannable UPI QR (Android UPI Setting:
// "C - UPI QRCODE In Bill" prints it on digital-tender bills, "D - … (ALL)"
// prints it on every bill). Needs a configured UPI VPA.
export function shouldPrintUpiQr(tx: Transaction, config: PosConfig): boolean {
  if (!upiConfigured(config)) return false;
  const settings = getAppSettingsSync();
  if (isOn(settings, 'upi_qrcode_in_bill_all')) return true;
  if (!isOn(settings, 'upi_qrcode_in_bill')) return false;
  return tx.paymentMethod === 'digital' || (tx.payments ?? []).some((p) => p.method === 'digital');
}

// upi://pay?pa=<vpa>&pn=<payee>&am=<amount>&cu=INR&tn=<note>
export function upiUri(config: PosConfig, amount: number, note?: string): string {
  const params = new URLSearchParams();
  params.set('pa', config.upiId!.trim());
  params.set('pn', (config.upiPayeeName || config.storeName).trim());
  if (amount > 0) params.set('am', amount.toFixed(2));
  params.set('cu', 'INR');
  if (note) params.set('tn', note);
  return `upi://pay?${params.toString()}`;
}

// Render a QR matrix as a crisp standalone SVG string (for receipt HTML).
export function qrSvgString(text: string, sizePx = 140): string {
  const matrix = generateQrMatrix(text, 'M');
  const n = matrix.length;
  const quiet = 2;
  const total = n + quiet * 2;
  let rects = '';
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (matrix[y][x]) rects += `<rect x="${x + quiet}" y="${y + quiet}" width="1" height="1"/>`;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${sizePx}" height="${sizePx}" viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges"><rect width="${total}" height="${total}" fill="#fff"/><g fill="#000">${rects}</g></svg>`;
}
